import React from 'react';
import { useQuery } from '@tanstack/react-query';
import type { PosSettings, ReceiptTemplateSettings } from '@/types';
import { Eye, Settings, Printer, Share2, ScrollText } from 'lucide-react';
import Receipt from '@/components/pos/Receipt';
import { authApi, loyaltyApi, posApi } from '@/services/api';
import {
  RECEIPT_PRESETS,
  normalizeLayoutMode,
  parseReceiptTemplateSettings,
  type ReceiptLayoutMode,
} from '@/utils/receiptTemplate';
import { setLocaleContext } from '@/utils/currency';
import { isUk } from '@/data/locales';

interface Props {
  settings?: ReceiptTemplateSettings | string;
  onChange: (newSettings: ReceiptTemplateSettings) => void;
}

export default function ReceiptTemplateDesigner({ settings, onChange }: Props) {
  const currentSettings = React.useMemo(
    () => parseReceiptTemplateSettings(settings),
    [settings],
  );
  const layoutMode = normalizeLayoutMode(currentSettings.layout_mode);

  const { data: profile } = useQuery({
    queryKey: ['companyProfile', 'receiptDesigner'],
    queryFn: async () => {
      const res = await authApi.getCompanyProfile();
      return res.data.message;
    },
  });

  const { data: posSettings } = useQuery({
    queryKey: ['posSettings', 'receiptDesigner'],
    queryFn: async () => {
      const res = await posApi.getPosSettings();
      return res.data.message as PosSettings;
    },
  });

  const { data: loyaltySettings } = useQuery({
    queryKey: ['loyaltySettings', 'receiptDesigner'],
    queryFn: async () => (await loyaltyApi.getSettings()).data?.message,
    staleTime: 60_000,
    retry: 1,
  });
  const loyaltyEnabled = Boolean(
    loyaltySettings?.is_active ?? loyaltySettings?.enabled ?? loyaltySettings?.loyalty_enabled,
  );

  React.useEffect(() => {
    if (profile?.country || profile?.currency) {
      setLocaleContext({
        country: profile.country || 'GB',
        currency: profile.currency || 'GBP',
      });
    }
  }, [profile?.country, profile?.currency]);

  const updateSetting = <K extends keyof ReceiptTemplateSettings>(
    key: K,
    value: ReceiptTemplateSettings[K],
  ) => {
    onChange({ ...currentSettings, [key]: value });
  };

  const applyPreset = (id: ReceiptLayoutMode) => {
    const preset = RECEIPT_PRESETS.find((p) => p.id === id);
    if (!preset) return;
    onChange({ ...currentSettings, ...preset.apply, layout_mode: id });
  };

  const mockInvoice = {
    company_logo: profile?.company_logo || 'https://placehold.co/400x200/slate/white?text=LOGO',
    company_name: profile?.company_name || 'Your Company',
    company_address: profile?.address || '',
    company_phone: profile?.mobile || '',
    company_email: profile?.email || '',
    name: 'INV-2026-0042',
    creation: new Date().toISOString(),
    customer_name: 'Sample Customer',
    cashier_name: 'Cashier',
    items: [
      { item_code: 'SKU-001', item_name: 'Sample Product A', quantity: 2, rate: 50, amount: 100, tax_rate: 20 },
      { item_code: 'SKU-002', item_name: 'Sample Product B', quantity: 1, rate: 25, amount: 25, tax_rate: 0 },
    ],
    item_total: 125,
    service_total: 0,
    discount_amount: loyaltyEnabled ? 7 : 5,
    tax_amount: 20,
    net_total: loyaltyEnabled ? 138 : 140,
    paid_amount: loyaltyEnabled ? 138 : 140,
    balance_amount: 0,
    previous_outstanding: 50,
    ...(loyaltyEnabled
      ? {
          loyalty: {
            earned: 14,
            redeemed: 200,
            balance: 1250,
            loyalty_discount: 2,
          },
        }
      : {}),
    fbr_invoice_number: profile?.country === 'PK' && profile?.enable_fbr_integration ? 'FBR-DEMO-001' : undefined,
    fbr_usin: profile?.country === 'PK' && profile?.enable_fbr_integration ? 'USIN-DEMO' : undefined,
    fbr_status: profile?.country === 'PK' && profile?.enable_fbr_integration ? 'Submitted' : undefined,
    fbr_qr_code: profile?.country === 'PK' && profile?.enable_fbr_integration ? 'FBR-DEMO-QR' : undefined,
    zatca_uuid: profile?.country === 'SA' && profile?.enable_zatca_integration ? 'ZATCA-DEMO-UUID' : undefined,
    zatca_status: profile?.country === 'SA' && profile?.enable_zatca_integration ? 'Submitted' : undefined,
    zatca_qr_code: profile?.country === 'SA' && profile?.enable_zatca_integration ? 'WldTZWxsZXI=' : undefined,
    zatca_icv: profile?.country === 'SA' && profile?.enable_zatca_integration ? 1 : undefined,
  };

  const previewPos = {
    ...(posSettings || {}),
    country: profile?.country || posSettings?.country || 'GB',
    currency: profile?.currency || posSettings?.currency || 'GBP',
    tax_registration_id: profile?.tax_registration_id || posSettings?.tax_registration_id,
    company_name_ar: profile?.company_name_ar,
    fbr_ntn: profile?.fbr_ntn || posSettings?.fbr_ntn,
    fbr_strn: profile?.fbr_strn || posSettings?.fbr_strn,
    enable_fbr_integration: profile?.enable_fbr_integration ?? posSettings?.enable_fbr_integration,
    enable_zatca_integration: profile?.enable_zatca_integration ?? posSettings?.enable_zatca_integration,
    zatca_vat_number: profile?.zatca_vat_number,
    zatca_company_name_ar: profile?.zatca_company_name_ar,
    uae_trn: profile?.uae_trn,
    ui_language: profile?.ui_language || posSettings?.ui_language,
    prices_include_tax: !!(profile?.prices_include_tax ?? posSettings?.prices_include_tax),
    tax_label: profile?.tax_label || posSettings?.tax_label || 'Tax',
    receipt_template_settings: currentSettings,
  } as PosSettings;

  const scaleClass =
    layoutMode === 'compact' || layoutMode === 'short' || layoutMode === 'modern'
      ? 'scale-100'
      : 'scale-[0.55] sm:scale-75 md:scale-90 lg:scale-[0.6] xl:scale-75 mb-[-30%]';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
      <div className="lg:col-span-5 bg-white border border-gray-100 shadow-sm rounded-xl p-5 space-y-5">
        <div className="flex items-center gap-2 border-b border-gray-100 pb-3">
          <Settings className="w-5 h-5 text-primary-500" />
          <h3 className="font-semibold text-gray-900">Customization Panel</h3>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            Template presets
          </label>
          <p className="text-xs text-gray-500 mb-3">
            Pick a preset, then tweak toggles. Paper Saver / Compact suit thermal printers; Modern Digital is best for WhatsApp image share.
          </p>
          <div className="grid grid-cols-1 gap-2">
            {RECEIPT_PRESETS.map((preset) => {
              const active = layoutMode === preset.id;
              const Icon =
                preset.id === 'modern' ? Share2
                  : preset.id === 'short' || preset.id === 'compact' ? Printer
                    : ScrollText;
              return (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => applyPreset(preset.id)}
                  className={`text-left rounded-xl border px-3 py-2.5 transition-all ${active
                    ? 'border-primary-400 bg-primary-50 ring-1 ring-primary-200'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                >
                  <div className="flex items-start gap-2.5">
                    <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${active ? 'text-primary-600' : 'text-gray-400'}`} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900">{preset.name}</span>
                        <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400 bg-gray-50 border border-gray-100 rounded px-1.5 py-0.5">
                          {preset.bestFor}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 leading-snug">{preset.description}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Font Size</label>
          <select
            value={currentSettings.font_size}
            onChange={(e) => updateSetting('font_size', e.target.value as ReceiptTemplateSettings['font_size'])}
            className="input-field py-2 text-sm"
          >
            <option value="small">Small</option>
            <option value="medium">Medium</option>
            <option value="large">Large</option>
          </select>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Receipt Header Text</label>
            <textarea
              rows={2}
              value={currentSettings.header_text}
              onChange={(e) => updateSetting('header_text', e.target.value)}
              placeholder="e.g. Welcome to our store!"
              className="input-field text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Receipt Footer Text</label>
            <textarea
              rows={2}
              value={currentSettings.footer_text}
              onChange={(e) => updateSetting('footer_text', e.target.value)}
              placeholder="e.g. Terms, Return policy, Thanks message"
              className="input-field text-sm"
            />
          </div>
        </div>

        <div className="space-y-3 pt-3 border-t border-gray-100">
          <label className="flex items-center justify-between p-1 cursor-pointer">
            <span className="text-sm font-medium text-gray-700">Show Company Logo</span>
            <input
              type="checkbox"
              checked={!!currentSettings.show_logo}
              onChange={(e) => updateSetting('show_logo', e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
          </label>

          {currentSettings.show_logo && (
            <div className="pl-4 pb-2 border-l-2 border-gray-100 space-y-1.5">
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>Logo Width</span>
                <span>{currentSettings.logo_width}px</span>
              </div>
              <input
                type="range"
                min="60"
                max="300"
                step="10"
                value={currentSettings.logo_width}
                onChange={(e) => updateSetting('logo_width', parseInt(e.target.value, 10))}
                className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
              />
            </div>
          )}

          {([
            ['show_served_by', 'Show Served By (Cashier)'],
            ['show_customer', 'Show Customer Name'],
            ['show_item_code', 'Show Item Code in Table'],
            ['show_item_price', 'Show Item Price in Table'],
            ['show_totals', 'Show Totals Section'],
            ['show_previous_outstanding', 'Previous Outstanding'],
            ['show_phone', 'Show Phone Number'],
            ['show_email', 'Show Email Address'],
            ['show_address', 'Show Address'],
          ] as const).map(([key, label]) => (
            <label key={key} className="flex items-center justify-between p-1 cursor-pointer">
              <span className="text-sm font-medium text-gray-700">{label}</span>
              <input
                type="checkbox"
                checked={!!currentSettings[key]}
                onChange={(e) => updateSetting(key, e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
            </label>
          ))}

          {isUk(profile?.country) && (
            <label className="flex items-center justify-between p-1 cursor-pointer">
              <span className="min-w-0">
                <span className="block text-sm font-medium text-gray-700">Show VAT Rate</span>
                <span className="block text-xs text-gray-500 mt-0.5">
                  Adds a VAT Rate column (S / R / Z) on invoice Print &amp; Download PDF
                </span>
              </span>
              <input
                type="checkbox"
                checked={!!currentSettings.show_vat_rate}
                onChange={(e) => updateSetting('show_vat_rate', e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 shrink-0 ml-3"
              />
            </label>
          )}

          {loyaltyEnabled && (
            <div className="space-y-3 pt-3 border-t border-gray-100">
              <p className="text-xs font-semibold uppercase tracking-wide text-teal-700">Loyalty on receipt</p>
              <p className="text-[11px] text-gray-500 -mt-1">
                Redeemed points always print as “Loyalty points redeem”. Choose whether to also show points earned and the new balance.
              </p>
              <label className="flex items-center justify-between p-1 cursor-pointer">
                <span className="text-sm font-medium text-gray-700">Show points earned</span>
                <input
                  type="checkbox"
                  checked={currentSettings.show_loyalty_earned !== false}
                  onChange={(e) => updateSetting('show_loyalty_earned', e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
              </label>
              <label className="flex items-center justify-between p-1 cursor-pointer">
                <span className="text-sm font-medium text-gray-700">Show points balance (total)</span>
                <input
                  type="checkbox"
                  checked={currentSettings.show_loyalty_balance !== false}
                  onChange={(e) => updateSetting('show_loyalty_balance', e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
              </label>
            </div>
          )}
        </div>
      </div>

      <div className="lg:col-span-7 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-gray-500 text-sm font-medium">
            <Eye className="w-4 h-4 text-emerald-500" />
            <span>Live preview ({RECEIPT_PRESETS.find((p) => p.id === layoutMode)?.name})</span>
          </div>
          <span className="text-xs bg-emerald-50 text-emerald-700 font-semibold px-2.5 py-0.5 rounded-full border border-emerald-100">
            {profile?.country || 'GB'} · {profile?.currency || 'GBP'}
          </span>
        </div>

        <div className="bg-gray-100 p-6 rounded-2xl border border-gray-200/50 shadow-inner flex justify-center overflow-x-auto relative min-h-[400px]">
          <div className={`transition-all duration-300 ease-in-out flex justify-center items-start origin-top ${scaleClass}`}>
            <Receipt
              invoiceName="mock"
              previewInvoice={mockInvoice}
              previewSettings={currentSettings}
              previewPosSettings={previewPos}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
